'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TaskViewer, GeneralNotFound } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getTask } from 'src/api/admin/tasks';
import { getGroupTaskDocuments } from 'src/api/admin/groups';

export const Route = createFileRoute('/_admin/groups/$groupId/approvals/$approvalId')({
	loader: async ({ params }) => {
		const { groupId, approvalId } = params;
		const task = await getTask(approvalId);

		if (!task) {
			return { task: undefined, documents: [] };
		}

		// For group approvals, fetch documents using groupId
		const documents = await getGroupTaskDocuments(groupId, task.documentIds || []);
		return { task, documents };
	},
	component: ApprovalDetailPage,
});

function ApprovalDetailPage() {
	const { groupId, approvalId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { task: initialTask, documents: initialDocuments } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const { data: task, refetch: refetchTask } = useQuery({
		queryKey: ['task', approvalId],
		queryFn: () => getTask(approvalId),
		initialData: initialTask,
	});

	const { data: documents } = useQuery({
		queryKey: ['group-task-documents', groupId, task?.documentIds],
		queryFn: () => getGroupTaskDocuments(groupId, task?.documentIds || []),
		initialData: initialDocuments,
		enabled: !!task,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const handleUpdate = () => {
		refetchTask();
		queryClient.invalidateQueries({ queryKey: ['group-approvals', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group', groupId] });
		queryClient.invalidateQueries({ queryKey: ['groups'] });
	};

	const handleClose = () => {
		navigate({ to: '/groups/$groupId/approvals', params: { groupId }, search: { page: undefined, sort: undefined } });
	};

	const handleOpenDocument = (documentId: string) => {
		window.open(`/groups/${groupId}/documents/${documentId}`, '_blank');
	};

	if (!task) {
		return (
			<GeneralNotFound
				label="We couldn't find this approval. Maybe it has been deleted?"
				action={{
					label: 'Return to approvals',
					href: `/groups/${groupId}/approvals`,
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<TaskViewer
			task={task}
			documents={documents || []}
			onUpdate={handleUpdate}
			onClose={handleClose}
			onOpenDocument={handleOpenDocument}
			permissions={{
				taskDelete: verifyPermission(PERMISSIONS.adminGroupTasksDelete),
				taskDo: task.executePermission?.isAllowed,
			}}
		/>
	);
}
